'use client';

import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/lib/context/user-context';
import { useI18n } from '@/lib/i18n';

export type Language = 'en' | 'zh' | 'es' | 'ja' | 'ko' | 'fr' | 'de' | 'pt' | 'ru';

interface LanguageOption {
  value: Language;
  labelKey: string;
  fallback: string;
  flag: string;
}

// Language options matching onboard-dialog.tsx NATIVE_LANGUAGE_OPTION_DEFS
const LANGUAGE_OPTION_DEFS: LanguageOption[] = [
  { value: 'en', labelKey: 'persona.languages.native.en', fallback: 'English', flag: '🇺🇸' },
  { value: 'zh', labelKey: 'persona.languages.native.zh', fallback: 'Chinese', flag: '🇨🇳' },
  { value: 'es', labelKey: 'persona.languages.native.es', fallback: 'Spanish', flag: '🇪🇸' },
  { value: 'ja', labelKey: 'persona.languages.native.ja', fallback: 'Japanese', flag: '🇯🇵' },
  { value: 'ko', labelKey: 'persona.languages.native.ko', fallback: 'Korean', flag: '🇰🇷' },
  { value: 'fr', labelKey: 'persona.languages.native.fr', fallback: 'French', flag: '🇫🇷' },
  { value: 'de', labelKey: 'persona.languages.native.de', fallback: 'German', flag: '🇩🇪' },
  { value: 'pt', labelKey: 'persona.languages.native.pt', fallback: 'Portuguese', flag: '🇵🇹' },
  { value: 'ru', labelKey: 'persona.languages.native.ru', fallback: 'Russian', flag: '🇷🇺' },
] as const;

export const useLanguageSettings = () => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('zh');
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n();
  // Note: Native Language and UI Language are now independent settings
  // Native Language is for learning target (affects vocabulary, AI explanations, etc.)
  // UI Language is controlled separately via language-toggle component

  const { user } = useUser();

  // Removed clearMessages function since we're not using Toast notifications

  // Transform language option defs to options with translated labels
  const languageOptions = LANGUAGE_OPTION_DEFS.map((def) => ({
    value: def.value,
    label: t(def.labelKey, def.fallback),
    flag: def.flag,
  }));

  // Load native language setting from API (independent from UI language)
  useEffect(() => {
    const loadLanguageSetting = async () => {
      if (!user?.id) {
        const defaultLang = 'zh';
        setCurrentLanguage(defaultLang);
        return;
      }

      try {
        const response = await fetch(`/api/user-settings?userId=${user.id}`);
        if (!response.ok) {
          throw new Error('Failed to load language setting');
        }

        const settings = await response.json();
        const language = settings.languageSetting || 'zh';
        // Validate that the loaded language is in our supported list
        const validLanguage = LANGUAGE_OPTION_DEFS.find(def => def.value === language)?.value || 'zh';
        setCurrentLanguage(validLanguage);
        // Note: We don't sync with UI language - they are independent
      } catch (err) {
        console.error('Error loading language setting:', err);
        const fallbackLang = 'zh';
        setCurrentLanguage(fallbackLang);
      }
    };

    loadLanguageSetting();
  }, [user]);

  // Update native language setting (independent from UI language)
  const updateLanguage = useCallback(async (language: Language) => {
    // Update local state immediately for better UX
    setCurrentLanguage(language);
    // Note: We don't sync with UI language - they are independent

    if (!user?.id) {
      return;
    }

    try {
      setIsLoading(true);

      // Save to API
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'languageSetting',
          value: language,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save language setting: ${response.status}`);
      }

      console.log('Language setting saved successfully');
    } catch (err) {
      console.error('Error updating language setting:', err);
      // Keep local change even on error for better UX
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  return {
    currentLanguage,
    isLoading,
    updateLanguage,
    languageOptions,
  };
};